import 'animate.css';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Auth from './pages/Auth'
import Calendar from './components/Onboarding/Gcalendar/Calendar';
import ErrorBoundary from './components/ErrorBoundary';
import useAuth from './hooks/useAuth';
import NotFound from './pages/404';
import OnBoarding from './pages/OnBoarding';
import OnBoardingV2 from './pages/OnBoardingV2';
import Redirect from './pages/Redirect';
import Profile from './pages/profile';
import TravelAndLocation from './pages/TravelAndLocation';
import BookingAvailability from './pages/BookingAvailability';
import GoogleCalendar from './pages/GoogleCalendar';
import ApiKeys from './pages/ApiKeys';
import UpdateEmail from './components/profile/UpdateEmail';
import CheckoutCallback from './components/Checkout/CheckoutCallback';
import SubscriptionCallback from './components/Subscription/SubscriptionCallback';
import ChannelManager from './components/Channels/ChannelManager';
import ChannelsPhonePurchaseSuccess from './pages/channels/PhonePurchaseSuccess';
import Contacts from './pages/contacts';
import PublicShare from './components/PublicShare/PublicShare'
import { ThemeProvider } from './theme/ThemeProvider';
import { useAppSelector } from './store';
import { SnackbarProvider, closeSnackbar, useSnackbar } from 'notistack'
import { styled } from '@mui/system';
import ConversationsWrapper from './components/Channels/ConversationsWrapper';
import Layout from './pages/layout';
import { getPusherInstance, PusherContext } from './services/auth.service';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import LottieWysiwyg from './components/Lottie/LottieWysiwyg';
import Playground from './components/Playground/Playground';
import Deck from './components/Deck/Deck';
import UserManager from './pages/dashboard/UserManager';
import GoogleCalendarCallback from './pages/GoogleCalendarCallback';
import AnalyticsPage from './pages/analytics';
import TimelineWrapper from './components/Timeline/TimelineWrapper';
import React, { useEffect, useState, useCallback } from 'react';
import { BoardPage, ChooseBoardPage } from './pages/boards';
import ContactDrawerProvider from './components/Contacts/ContactDrawer';
import { ChooseYourPlanPage } from './pages/onboarding/v1';
import { CompleteYourProfilePage, ConfirmBusinessPhoneNumberPage, ConfirmEmailOnboardingPage, ConfirmPhoneCodeOnboardingPage, ConfirmPhoneOnboardingPage, EnterTheCodeWeJustSentPage, ForgotPasswordOnboardingPage, InsertBusinessPhoneNumberPage, InsertBusinessWebsiteOnboardingPage, LoginOnboardingPage, PersonalizeYourAssistantPage, ShapeYourFirstAssistantPage } from './pages/onboarding/v2';
import TrialSetupComplete from './pages/onboarding/v2/TrialSetupComplete';
import BillingAndPlans from './pages/BillingAndPlans';
import PublicBillingAndPlans from './pages/PublicBillingAndPlans';
import { ContactCreateDialog } from './components/Contacts/ContactCreateDialog';
import ConversationCreateDialogV2 from './components/Channels/ConversationCreateDialogV2';
import BillingGuard from './components/BillingGuard';
import BillingBanner from './components/BillingBanner';

// Lazy load the v2 oppy pages
const OppiesListPage = React.lazy(() => import('./features/oppies/v2/pages/OppiesListPage'));
const OppyEditorPage = React.lazy(() => import('./features/oppies/v2/pages/OppyEditorPage'));

const params = new URLSearchParams(window.location.search)

const setCookies = (params: URLSearchParams) => {
  if (params.get("sign_in_redirect") != null || params.get("sign_in_redirect")?.length === 0) {
    document.cookie = `rmk_sign_in_redirect=${params.get('sign_in_redirect')}`;
  }
  if (params.get("category") != null || params.get("category")?.length === 0) {
    document.cookie = `rmk_category=${params.get('category')}`;
  }
  if (params.get("ref") != null || params.get("ref")?.length === 0) {
    document.cookie = `rmk_ref=${params.get('ref')}`;
  }
  if (params.get("account_invite") != null || params.get("account_invite")?.length === 0) {
    document.cookie = `rmk_account_invite=${params.get('account_invite')}`;
  }
}

setCookies(params)

const OppySnackbarProvider = styled(SnackbarProvider)(() => ({
  '& .MuiSnackbar-root': {
    maxWidth: '100px',
  },
}));

const snackbarAction = (snackbarId: string | number | undefined) => {
  return (
    <IconButton onClick={() => closeSnackbar(snackbarId)}>
      <CloseIcon />
    </IconButton>
  );
}

// Keyboard shortcut wrapper component
const KeyboardShortcutWrapper = ({ children }: { children: React.ReactNode }) => {
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [conversationDialogOpen, setConversationDialogOpen] = useState(false);
  const [conversationInitialTab, setConversationInitialTab] = useState<'email' | 'sms' | 'phone'>('email');
  const { channels = [] } = useAppSelector(state => state.channels);
  const { isLoggedIn } = useAuth();
  const { user } = useAppSelector(state => state.user);
  const defaultChannelId = channels.length > 0 ? channels[0].id : null;

  const handleKeyPress = useCallback((event: KeyboardEvent) => {
    // Check if user is in an input, textarea, or contenteditable
    const target = event.target as HTMLElement;
    const isInInput =
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.contentEditable === 'true' ||
      target.closest('[contenteditable="true"]');

    // Don't trigger if user is typing in an input or if meta/ctrl key is pressed
    if (isInInput || event.metaKey || event.ctrlKey || event.altKey) {
      return;
    }

    const key = event.key.toLowerCase();

    switch(key) {
      case 'c':
        // Open create contact dialog
        setContactDialogOpen(true);
        event.preventDefault();
        break;
      case 'e':
        // Open create conversation dialog focused on email
        setConversationInitialTab('email');
        setConversationDialogOpen(true);
        event.preventDefault();
        break;
      case 's':
        // Open create conversation dialog focused on SMS
        setConversationInitialTab('sms');
        setConversationDialogOpen(true);
        event.preventDefault();
        break;
      case 'p':
        // Open create conversation dialog focused on phone
        setConversationInitialTab('phone');
        setConversationDialogOpen(true);
        event.preventDefault();
        break;
    }
  }, []);

  useEffect(() => {
    // Only add listener if user is logged in
    if (isLoggedIn && user) {
      window.addEventListener('keydown', handleKeyPress);
      return () => {
        window.removeEventListener('keydown', handleKeyPress);
      };
    }
  }, [handleKeyPress, isLoggedIn, user]);

  return (
    <>
      {children}

      {/* Only render dialogs if user is logged in */}
      {isLoggedIn && user && (
        <>
          {/* Contact Create Dialog */}
          <ContactCreateDialog
            open={contactDialogOpen}
            onClose={() => setContactDialogOpen(false)}
          />

          {/* Conversation Create Dialog */}
          <ConversationCreateDialogV2
            open={conversationDialogOpen}
            handleClose={() => setConversationDialogOpen(false)}
            incomingChannelId={defaultChannelId}
            initialMethod={conversationInitialTab}
          />
        </>
      )}
    </>
  );
};

const AppContent = () => {
  const { isLoggedIn, authLoading } = useAuth();
  const { user } = useAppSelector(state => state.user);
  //const { enqueueSnackbar } = useSnackbar();

  // useEffect(() => {
  //   enqueueSnackbar('Server update in progress. App pages appear empty - rest assured, All messaging and comms systems are operating as usual. Check back shortly for full functionality.', {
  //     variant: 'info',
  //     persist: true,
  //     anchorOrigin: { vertical: 'top', horizontal: 'center' }
  //   });
  // }, [enqueueSnackbar]);

  return (
    <>
      {/* {authLoading && <Loading/>} */}
      <div className="App">
        <ThemeProvider>
          <PusherContext.Provider value={getPusherInstance()}>
            <OppySnackbarProvider
              autoHideDuration={3000}
              anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
              action={snackbarAction}
            >
              <Router>
                <BillingGuard>
                  <BillingBanner />
                  <ContactDrawerProvider>
                    <KeyboardShortcutWrapper>
                      <Routes>
                      <Route path="/lottie" element={<LottieWysiwyg key="lottie" />} />
                      <Route path="/" element={<ErrorBoundary><Auth screen={"register"} /></ErrorBoundary>} />
                    {/* <Route path="/pricing" element={<PricingPage />} /> */}
                    <Route path="/s/:channel_embed_key" element={<PublicShare />} />
                    <Route path="/profile/:email_token" element={<UpdateEmail />} />
                    <Route path="/checkout/cancel" element={<CheckoutCallback />} />
                    <Route path="/subscription/success" element={<SubscriptionCallback />} />

                    {/* Onboarding flows V1 */}

                    {/* Onboarding flows V2 */}
                    <Route path="/trial-setup-complete" element={<TrialSetupComplete />} />
                    <Route path="/onboarding/v2/login" element={<LoginOnboardingPage />} />
                    <Route path="/onboarding/v2/forgot-password" element={<ForgotPasswordOnboardingPage />} />
                    <Route path="/onboarding/v2/phone-number" element={<ConfirmPhoneOnboardingPage />} />
                    <Route path="/onboarding/v2/confirm-phone-code" element={<ConfirmPhoneCodeOnboardingPage />} />
                    <Route path="/onboarding/v2/insert-business-website" element={<InsertBusinessWebsiteOnboardingPage />} />
                    <Route path="/onboarding/v2/confirm-informations" element={<ConfirmBusinessPhoneNumberPage />} />
                    <Route path="/onboarding/v2/insert-business-phone-number" element={<InsertBusinessPhoneNumberPage />} />
                    <Route path="/onboarding/v2/confirm-business-phone-number" element={<ConfirmBusinessPhoneNumberPage />} />
                    <Route path="/onboarding/v2/shape-your-first-assistant" element={<ShapeYourFirstAssistantPage />} />
                    <Route path="/onboarding/v2/personalize-your-assistant" element={<PersonalizeYourAssistantPage />} />
                    <Route path="/onboarding/v2/complete-your-profile" element={<CompleteYourProfilePage />} />
                    <Route path="/onboarding/v2/confirm-email" element={<ConfirmEmailOnboardingPage />} />
                    <Route path="/onboarding/v2/enter-the-code-we-just-sent" element={<EnterTheCodeWeJustSentPage />} />
                    <Route path="/deck" element={<Deck />} />
                    {!(isLoggedIn && user) ? (
                      <>
                        <Route path="/login" element={<ErrorBoundary><Auth screen={"login"} /></ErrorBoundary>} />
                        <Route path="/register" element={<ErrorBoundary><Auth screen={"register"} /></ErrorBoundary>} />
                        <Route path="/on-boarding-v2" element={<OnBoardingV2 />} />
                        <Route path="/forgot_password" element={<ErrorBoundary><Auth screen={"forgot_password"} /></ErrorBoundary>} />
                        <Route path="/reset_password" element={<ErrorBoundary><Auth screen={"reset_password"} /></ErrorBoundary>} />
                        <Route path="/calendar-callback" element={<GoogleCalendarCallback />} />
                        <Route path="/choose-your-plan" element={<ChooseYourPlanPage />} />
                        <Route path="/pricing" element={<PublicBillingAndPlans />} />
                        <Route path="*" element={<Redirect isLoggedIn={isLoggedIn} authLoading={authLoading} />} />
                      </>
                    ) : (
                      <>
                        <Route path="*" element={<Redirect isLoggedIn={isLoggedIn} authLoading={authLoading} />} />
                        <Route path="/404" element={<NotFound />} />
                        <Route path="/on-boarding" element={<Layout components={[<OnBoarding key="on-boarding" />]} />} />
                        <Route path="/calendar" element={<Layout components={[<Calendar key="calendar" />]} />} />
                        <Route path="/dashboard" element={<Layout components={[<UserManager key="user-manager" user={user} />]} />} />
                        <Route path="/channels" element={<Layout components={[<ChannelManager key="channels" />]} />} />
                        <Route path="/contacts" element={<Layout components={[<Contacts key="contacts" />]} />} />
                        <Route path="/playground" element={<Layout components={[<Playground key="playground" />]} />} />
                        <Route path="/channels/phone_purchase_success" element={<Layout components={[<ChannelsPhonePurchaseSuccess key="channels-phone-purchase-success" />]} />} />
                        <Route path="/conversations" element={<Layout components={[<ConversationsWrapper key="conversations-wrapper" />]} />} />
                        <Route path="/profile" element={<Profile key="profile" />} />
                        <Route path="/billing-and-plans" element={<Layout components={[<BillingAndPlans key="billing-and-plans" />]} />} />
                        <Route path="/travel-and-location" element={<Layout components={[<TravelAndLocation key="travel-and-location" />]} />} />
                        <Route path="/booking-availability" element={<Layout components={[<BookingAvailability key="booking-availability" />]} />} />
                        <Route path="/google-calendar" element={<Layout components={[<GoogleCalendar key="google-calendar" />]} />} />
                        <Route path="/api-keys" element={<ApiKeys key="api-keys" />} />
                        <Route path="/analytics" element={<AnalyticsPage />} />
                        <Route path="/timeline" element={<Layout components={[<TimelineWrapper key="timeline-wrapper" />]} />} />
                        <Route path="/boards-list" element={<ChooseBoardPage />} />
                        <Route path="/boards/:board_id" element={<BoardPage />}/>
                        <Route path="/v2/oppies" element={<Layout components={[<React.Suspense key="oppies-list" fallback={<div>Loading...</div>}><OppiesListPage /></React.Suspense>]} />} />
                        <Route path="/v2/oppies/:oppyId" element={<Layout components={[<React.Suspense key="oppy-editor" fallback={<div>Loading...</div>}><OppyEditorPage /></React.Suspense>]} />} />
                      </>
                    )}
                      </Routes>
                    </KeyboardShortcutWrapper>
                  </ContactDrawerProvider>
                </BillingGuard>
              </Router>
            </OppySnackbarProvider>
          </PusherContext.Provider>
        </ThemeProvider>
      </div>
    </>
  );
};

const App = () => {
  return (
    <ThemeProvider>
      <OppySnackbarProvider
        autoHideDuration={3000}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        action={snackbarAction}
      >
        <AppContent />
      </OppySnackbarProvider>
    </ThemeProvider>
  );
};

export default App;
